import { FC, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { Backup, Delete } from '@mui/icons-material';
import { t } from 'i18next';
import { fileSizeLabelFormat } from '@util/fileSizeLabelFormat.ts';
import { theme } from '@store';
import { TruncatedTextWithConditionalTooltip } from '../TruncatedTableRow';

type FormatType = 'csv' | 'documents' | 'template';

const FormatLabels: Record<FormatType, string> = {
  csv: 'CSV',
  documents: 'PDF, PNG, JPG, DOC, DOCX',
  template: 'DOCX',
};
const FormatOptions: Record<FormatType, string> = {
  csv: 'text/csv',
  documents: 'image/png, application/pdf, image/jpg, image/jpeg, .doc, .docx',
  template: '.docx',
};

const FileTypesAccept: Record<FormatType, string[]> = {
  csv: ['text/csv'],
  documents: [
    'image/png',
    'application/pdf',
    'image/jpg',
    'image/jpeg',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  template: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
};

interface FileUploaderProps {
  acceptFormat?: FormatType;
  maximumSizeInMB?: number;
  title?: string;
  errorMessage?: string;
  file: File | null;
  setFile: (file: File | null) => void;
}

const MB = 1000000;

export const FileUploader: FC<FileUploaderProps> = ({
  file,
  setFile,
  acceptFormat = 'csv',
  maximumSizeInMB = 25,
  title = t('common.fileUploader.title'),
  errorMessage,
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(event.target.files[0]);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      if (
        !FileTypesAccept[acceptFormat].includes(
          event.dataTransfer.files[0].type,
        )
      ) {
        toast.error(
          t('common.fileUploader.wrongFormat', {
            format: FormatLabels[acceptFormat],
          }),
        );
        return;
      }
      if (maximumSizeInMB * MB < event.dataTransfer.files[0].size) {
        toast.error(t('common.fileUploader.fileTooLarge'));
        return;
      }
      setFile(event.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: { preventDefault: () => void }) => {
    e.preventDefault();
  };

  const handleDragEnter = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const removeFile = () => {
    setFile(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type='file'
        name='myImage'
        accept={FormatOptions[acceptFormat]}
        onChange={handleInputChange}
        onDrop={handleDrop}
        style={{ display: 'none' }}
      />

      {file ? (
        <Box>
          {errorMessage && <>{errorMessage}</>}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              border: theme => `1px solid ${theme.palette.grey[400]}`,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TruncatedTextWithConditionalTooltip
                width='450px'
                text={file.name}
                variant='subtitle2'
              />
              <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                {fileSizeLabelFormat(file.size)}
              </Typography>
            </Box>
            <IconButton onClick={removeFile}>
              <Delete sx={{ height: 24, width: 24 }} />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            px: 3,
            height: 140,
            border: theme => `1px dashed ${theme.palette.grey[400]}`,
            cursor: 'pointer',
            backgroundColor: isDragging
              ? theme.palette.metallicBlue.main
              : 'inherit',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onClick={e => {
            e.preventDefault();
            if (inputRef && inputRef?.current && !file) {
              inputRef.current.click();
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 1,
            }}
          >
            <Backup sx={{ height: 24, width: 24 }} color='primary' />
            <Typography variant='subtitle1'>{title}</Typography>
            <Typography variant='body2' sx={{ color: 'text.disabled' }}>
              {t('common.fileUploader.subTitle', {
                format: FormatLabels[acceptFormat],
                limit: maximumSizeInMB,
              })}
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};
